import settings from "@/core/settings"
import { sessionStore } from "./store"
import bus from "./api/bus"
import logger from "./logger"
import getCustomerId from "./customerId"
import { findCustomer } from "./tagging"
import { fetch, isUnreachable } from "@/core/fetch"

const key = "nosto:analytics:queue"

export type Data = {
  url: string
  data: object
}

type Flags = { delayed: boolean }

/**
 * Sends analytics data to the specified URL.
 */
export async function sendOrEnqueue(url: string, data: object, flags?: Flags) {
  if (!settings.testing && flags?.delayed) {
    enqueue({ url, data })
  } else {
    await send(url, data)
  }
}

async function send(baseUrl: string, data: object) {
  const customerId = await getCustomerId()
  const customerData = findCustomer()
  if (!customerId) {
    logger.warn(new Error("Skipping analytics event, no customer id defined"))
    return
  }
  const url = new URL(baseUrl)
  url.searchParams.set("merchant", settings.account)
  url.searchParams.set("c", customerId)
  if (customerData?.customer_reference) {
    url.searchParams.set("customerReference", customerData?.customer_reference)
  }

  await fetch(url.toString(), {
    method: "POST",
    body: JSON.stringify(data),
    headers: { "Content-Type": "text/plain" }
  })
}

/**
 * Sends all queued analytics data by making HTTP POST requests.
 */
export async function flushQueue() {
  let data: Data | undefined
  while ((data = dequeue())) {
    const start = Date.now()
    try {
      await send(data.url, data.data)
    } catch (e) {
      const duration = Date.now() - start
      logAnalyticsError("Failed to send analytics data", e, duration)
    }
  }
}

export function logAnalyticsError(msg: string, e: unknown, duration: number) {
  if (e instanceof Error) {
    // @ts-expect-error unmapped field
    e.reporter = "client:analytics"
  }
  if (isUnreachable(e)) {
    logger.warn(`${msg}, duration ${duration}ms`, e)
  } else {
    logger.error(`${msg}, duration ${duration}ms`, e)
  }
}

/**
 * Initializes the analytics module.
 */
export function initAnalytics() {
  if (!settings.testing) {
    bus.on("prerequest", flushQueue)
  }
}

function enqueue(data: Data) {
  const queue = sessionStore.getAsJson<Data[]>(key) || []
  queue.push(data)
  sessionStore.setAsJson(key, queue)
}

function dequeue() {
  const queue = sessionStore.getAsJson<Data[]>(key) || []
  if (queue.length) {
    const data = queue.shift()
    sessionStore.setAsJson(key, queue)
    return data
  }
}
